import { UserDecorator } from 'decorators'
import { map, concat, set, omit, filter, findIndex, sortBy } from 'lodash'
import { http, queryString } from 'utils'

// User mutation imports.
import {
  SET_AUTHORIZED, SET_USERS, RESET_USER, SET_USER, SET_USER_ATTRIBUTE, SET_USERS_PAGINATION,
  ADD_USERS, SET_PENDING_USERS, ADD_DOCUMENT, REMOVE_DOCUMENT, REPLACE_DOCUMENT, ADD_USER
} from 'vx/mutations'

const initialUserState = () => {
  return {
    id: null,
    email: null,
    mobile: null,
    role_id: null,
    parent_id: null,
    profile: {
      name: null,
      surname: null,
      company_name: null,
      company_phone: null,
      company_abn: null,
      address_line_1: null,
      address_line_2: null,
      state: null,
      suburb: null,
      postcode: null,
      commission_amount: null,
      account_name: null,
      account_number: null,
      bsb: null
    },
    driver_profile: {
      licence: null,
      licence_expiry: null
    },
    documents: []
  }
}

// User state.
const state = {
  authorized: false,
  user: initialUserState(),
  users: [],
  pendingUsers: []
}

// User state getters.
const getters = {
  /**
   * Authorized user state as a decorated object.
   */
  authorized: state => new UserDecorator(state.authorized),

  /**
   * User state as a decorated object.
   */
  user: state => new UserDecorator(state.user),

  /**
   * User options for drop down.
   */
  userOptions: state => map(state.users, user => ({ id: user.id, text: user.profile.company_name })),

  /**
   * User options for drop down.
   */
  userOptionsUsingNames: state => map(state.users, user => ({ id: user.id, text: user.profile.full_name })),

  /**
   * User options for drop down.
   */
  userOptionsWithPublic: (state, getters) => concat([{ id: '', text: '<em>Public Location</em>' }], getters.userOptions),

  /**
   * User options for drop down.
   */
  userOptionsWithNone: (state, getters) => concat([{ id: '', text: '<em>None</em>' }], getters.userOptions),

  /**
   * User options for drop down.
   */
  userOptionsWithAll: (state, getters) => concat([{ id: '', text: '<em>All</em>' }], getters.userOptions),

  /**
   * Users decorated via the UserDecorator.
   */
  usersDecorated: state => map(state.users, user => (new UserDecorator(user))),

  /**
   * Users decorated via the UserDecorator.
   */
  pendingUsersDecorated: state => map(state.pendingUsers, user => (new UserDecorator(user)))
}

// User actions.
const actions = {
  /**
   * Attempt to authorize a user by fetching the logged in user details.
   */
  authorize ({ commit, getters }) {
    return http.get('user').then(response => {
      commit(SET_AUTHORIZED, response.data)

      return getters.authorized
    })
  },

  /**
   * Log a user in and set the authorized user.
   */
  login ({ commit, getters }, { email, password }) {
    return http.post('auth/login', { email, password }).then(response => {
      commit(SET_AUTHORIZED, response.data.user)

      return response
    })
  },

  /**
   * Get a user.
   */
  getUser ({ commit }, id) {
    return http.get(`users/${id}`).then(response => {
      commit(SET_USER, response.data)

      return response
    })
  },

  /**
   * Register a new user with email, password, etc.
   */
  register ({ commit }, payload) {
    return http.post('auth/register', payload)
  },

  /**
   * Save registration details.
   */
  saveRegistrationDetails ({ commit }, { id, payload }) {
    return http.post(`auth/register-details/${id}`, payload).then(response => {
      commit(SET_AUTHORIZED, response.data)

      return response
    })
  },

  /**
   * Send a password reset token.
   */
  sendPasswordEmail (store, email) {
    return http.post(`auth/send-password-token`, { email })
  },

  /**
   * Reset a users password.
   */
  resetPassword (store, payload) {
    return http.post(`auth/reset-password`, payload)
  },

  /**
   * Send a email for user to verify their account.
   */
  sendVerificationMail (store, email) {
    return http.post(`auth/send-verification`, { email })
  },

  /**
   * Verify a user on the api.
   */
  attemptUserVerification (store, payload) {
    return http.post(`auth/verify-user`, payload)
  },

  /**
   * Get a list of users.
   */
  getUsers ({ commit }, payload) {
    return http.get(`users?${queryString(payload)}`).then(response => {
      // sort users by company name in alphabetic order
      commit(SET_USERS, sortBy(response.data.data, [function (o) { return o.profile.company_name }]))
      commit(SET_USERS_PAGINATION, omit(response.data, 'data'))

      return response
    })
  },

  /**
   * Get a list of users (client or carrier) that have yet to be verified.
   */
  getPendingUsers ({ commit }, payload) {
    return http.get(`users/pending?${queryString(payload)}`).then(response => {
      commit(SET_PENDING_USERS, response.data)

      return response
    })
  },

  /**
   * Get the users for the next page.
   */
  getUsersForNextPage ({ commit, state }, payload) {
    if (state.pagination.current_page === state.pagination.last_page) {
      return Promise.reject('There are no more users to load.')
    }

    let page = state.pagination.current_page + 1

    return http.get(`users?${queryString(payload)}&page=${page}`).then(response => {
      commit(ADD_USERS, response.data.data)
      commit(SET_USERS_PAGINATION, omit(response.data, 'data'))

      return response
    })
  },

  /**
   * Store a user.
   */
  storeUser ({ commit }, payload) {
    return http.post(`users`, payload)
  },

  /**
   * Update a user.
   */
  updateUser ({ commit }, { id, payload }) {
    return http.put(`users/${id}`, payload)
  },

  /**
   * Update a user password.
   */
  updateUserPassword (store, { id, payload }) {
    return http.post(`users/${id}/password`, payload)
  },

  /**
   * Archive a user by sending a delete request.
   */
  archiveUser ({ commit }, id) {
    return http.delete(`users/${id}`)
  },

  /**
   * Restore a user by sending a post request.
   */
  restoreUser ({ commit }, id) {
    return http.post(`users/${id}/restore`)
  },

  /**
   * Enable a user by sending a post request.
   */
  enableUser ({ commit }, id) {
    return http.post(`users/${id}/enable`)
  },

  /**
   * Disable a user by sending a post request.
   */
  disableUser ({ commit }, id) {
    return http.post(`users/${id}/disable`)
  },

  /**
   * Verify a user by sending a post request.
   */
  verifyUser ({ commit }, id) {
    return http.post(`users/${id}/verify`)
  },

  /**
   * Activate a carrier user by sending a post request.
   */
  activateCarrier ({ commit }, id) {
    return http.post(`users/${id}/activate-carrier`)
  },

  /**
   * Store a new insurance document.
   */
  storeInsuranceDocument ({ commit }, { id, insurance }) {
    let payload = new FormData()

    payload.append('file', insurance.file)
    payload.append('expiry', insurance.expiry)
    payload.append('title', insurance.title)
    payload.append('file_name', insurance.file_name)

    return http.post(`users/${id}/documents`, payload).then(response => {
      commit(ADD_DOCUMENT, response.data)

      return response
    })
  },

  /**
   * Update an existing insurance document and then replace that document
   * in the array with the updated document.
   */
  updateInsuranceDocument ({ commit }, { userId, documentId, insurance }) {
    let payload = new FormData()

    if (insurance.file !== null) {
      payload.append('file', insurance.file)
    }

    payload.append('expiry', insurance.expiry)
    payload.append('title', insurance.title)
    payload.append('file_name', insurance.file_name)

    return http.post(`users/${userId}/documents/${documentId}`, payload).then(response => {
      commit(REPLACE_DOCUMENT, response.data)

      return response
    })
  },

  /**
   * Delete a document.
   */
  deleteInsuranceDocument ({ commit }, { userId, documentId }) {
    return http.delete(`users/${userId}/documents/${documentId}`).then(response => {
      commit(REMOVE_DOCUMENT, documentId)

      return response
    })
  },

  /**
   * Update a user's driver details
   */
  updateDriverDetails ({ commit }, { userId, payload }) {
    return http.post(`users/${userId}/driver-details`, payload).then(response => {
      return response
    })
  },

  /**
   * unsubscribe a user from the public jobs daily email
   */
  unsubscribeUser ({ commit }, payload) {
    return http.post(`users/unsubscribe`, payload).then(response => {
      return response
    })
  },

  /**
   * Get User Parent Options
   */
  getParentOptions ({ commit }) {
    return http.get('users/parent-list')
  }
}

// User state mutations.
const mutations = {
  /**
  * Set the authorized user object.
  */
  [SET_AUTHORIZED] (state, user) {
    state.authorized = user
  },

  /**
   * Add more users to the users state.
   */
  [ADD_USERS] (state, users) {
    state.users = concat(state.users, users)
  },

  /**
  * Set the users state.
  */
  [SET_USERS] (state, users) {
    state.users = users
  },

  /**
   * Set the user state.
   */
  [SET_USER] (state, user) {
    state.user = user
  },

  /**
   * Set a user attribute to a given value.
   */
  [SET_USER_ATTRIBUTE] (state, { attribute, value }) {
    set(state.user, attribute, value)
  },

  /**
   * Reset the user state.
   */
  [RESET_USER] (state) {
    state.user = initialUserState()
  },

  /**
   * Set the users pagination.
   */
  [SET_USERS_PAGINATION] (state, pagination) {
    state.pagination = pagination
  },

  /**
   * Set the pending users state.
   */
  [SET_PENDING_USERS] (state, users) {
    state.pendingUsers = users
  },

  /**
   * Add a document to the users state.
   */
  [ADD_DOCUMENT] (state, document) {
    state.user.documents.push(document)
  },

  /**
   * Add a document to the users state.
   */
  [ADD_USER] (state, user) {
    state.users.push(user)
  },

  /**
   * Remove a document from the users state.
   */
  [REMOVE_DOCUMENT] (state, id) {
    state.user.documents = filter(state.user.documents, existing => existing.id !== id)
  },

  /**
  * After updating replace the document in the array.
  */
  [REPLACE_DOCUMENT] (state, document) {
    state.user.documents.splice(findIndex(state.user.documents, existing => existing.id === document.id), 1, document)
  }
}

// User nested modules.
import creditNote from 'vx/modules/user/credit-note'
import accountUsers from 'vx/modules/user/account-users'

const modules = {
  creditNote,
  accountUsers
}

// Export our state, getters, actions, mutations, and modules.
export default {
  namespaced: true, state, getters, actions, mutations, modules
}



// WEBPACK FOOTER //
// ./src/vuex/modules/user.js